import PaymentBreakdown from "../../paymentBreakdown";
import { PaymentBreakdownGroup } from "../../paymentBreakdown/types";
import { Text } from "../../../ui/text";
import { VStack } from "../../../ui/vstack";
import { PaymentBreakdownFragmentFragment } from "@repo/graphql/documents/payments/fragment.generated";
import { WorkOrderPaymentMethodEnum } from "@repo/graphql/gql/graphql";
import React, { useMemo } from "react";

import PaymentMethodSelector from "../paymentMethodSelector";
import WalletNote from "./WalletNote";

export type CheckoutSummaryOption = {
  method: WorkOrderPaymentMethodEnum;
  available: boolean;
  unavailableReason?: string | null;
  breakdown: PaymentBreakdownFragmentFragment;
};

type Props = {
  loading: boolean;
  options: CheckoutSummaryOption[];
  selected?: WorkOrderPaymentMethodEnum;
  onSelect: (method: WorkOrderPaymentMethodEnum) => void;
  labels: Partial<Record<WorkOrderPaymentMethodEnum, string>>;
  groups?: PaymentBreakdownGroup[];
  countryName?: string | null;
  /** Show the "no Montu fee" caption when the wallet is the selected
   * method. Defaults to on — both checkouts that use this component want
   * it, but a future consumer without a wallet option can opt out. */
  walletNote?: boolean;
  /** Pay button + any gating captions (tax profile, ambassador warnings) —
   * feature-specific, so callers own it instead of this component. */
  children?: React.ReactNode;
};

/** Method selector + priced breakdown, shared by the work-order brand
 * checkout and the affiliate payout checkout. */
const PaymentSummary = React.memo(
  ({
    loading,
    options,
    selected,
    onSelect,
    labels,
    groups = [],
    countryName,
    walletNote = true,
    children,
  }: Props) => {
    const selectedOption =
      options.find((option) => option.method === selected) ?? options[0];
    const lines = useMemo(
      () => selectedOption?.breakdown.lines ?? [],
      [selectedOption],
    );

    return (
      <VStack space="md" className="mt-4">
        <PaymentMethodSelector
          options={options}
          value={selectedOption?.method}
          onChange={onSelect}
          labels={labels}
        />
        <PaymentBreakdown lines={lines} groups={groups} loading={loading} />
        {walletNote &&
        selectedOption?.method === WorkOrderPaymentMethodEnum.Wallet ? (
          <WalletNote />
        ) : null}
        {countryName ? (
          <Text size="xs" action="muted">
            Impuestos calculados según el país de tu marca ({countryName})
          </Text>
        ) : null}
        {children}
      </VStack>
    );
  },
);

PaymentSummary.displayName = "PaymentSummary";

export default PaymentSummary;
